import React, { useState, useEffect } from "react";
import { utils, writeFile, write } from 'xlsx';
import { getFirestore, collection, getDocs, doc, updateDoc, setDoc, arrayUnion } from 'firebase/firestore';
import Container from '@mui/material/Container';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ButtonGroup from '@mui/material/ButtonGroup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircularProgress from '@mui/material/CircularProgress';
import { StyledTableCell, StyledTableRow } from '../../utils/styled'
import { idCuadrillaNucleo } from '../../utils/default'
import { formatDate } from "../../utils/dateUtils";
import MuiAlert from '@mui/material/Alert';
import { getCuadrillasOnce, invalidateCuadrillasCache } from "../../services/cuadrillasService";
import { getMaterialesOnce } from "../../services/materialsService";
import { recalculatePreQuantityFromCuadrillas } from "../../services/preStockService";
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { Box, Typography } from "@mui/material";
import moment from 'moment';

const firestore = getFirestore();

const currentUser = JSON.parse(localStorage.getItem("currentUser"));

// Estilos para la visualización de fechas de modificación
const serialStyles = {
    valueSerialContainer: {
        display: 'flex',
        flexDirection: 'column',
        marginBottom: '4px',
        padding: '2px 0'
    },
    modificationDate: {
        fontSize: '10px',
        color: '#666',
        fontStyle: 'italic',
        marginTop: '1px',
        lineHeight: '1.2'
    }
};

// Función para formatear fecha de última modificación de manera compacta
const formatModificationDate = (fechaUltimaModificacion) => {
    if (!fechaUltimaModificacion) return null;
    
    try {
        let date;
        
        // Si la fecha está en formato timestamp (número)
        if (typeof fechaUltimaModificacion === 'number') {
            date = new Date(fechaUltimaModificacion);
        }
        // Si es un objeto Date
        else if (fechaUltimaModificacion instanceof Date) {
            date = fechaUltimaModificacion;
        }
        // Si es string en formato ISO
        else if (typeof fechaUltimaModificacion === 'string' && fechaUltimaModificacion.includes('T')) {
            date = new Date(fechaUltimaModificacion);
        }
        // Si es string en formato YYYYMMDD
        else if (typeof fechaUltimaModificacion === 'string' && fechaUltimaModificacion.length === 8) {
            return formatDate(fechaUltimaModificacion);
        }
        // Si es string en formato DD/MM/YYYY
        else if (typeof fechaUltimaModificacion === 'string' && fechaUltimaModificacion.includes('/')) {
            return fechaUltimaModificacion;
        }
        // Intentar parsear como fecha
        else {
            date = new Date(fechaUltimaModificacion);
        }
        
        // Verificar que la fecha sea válida
        if (isNaN(date.getTime())) {
            return null;
        }
        
        // Formatear de manera compacta
        return date.toLocaleDateString('es-PY', { 
            day: '2-digit', 
            month: '2-digit', 
            year: '2-digit'
        }) + ' ' + date.toLocaleTimeString('es-PY', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
        
    } catch (error) {
        console.warn('Error formateando fecha de modificación:', error);
        return null;
    }
};

const getSerialAge = (fechaUltimaModificacion) => {
    if (!fechaUltimaModificacion) return 0;
    try {
        let date;
        if (typeof fechaUltimaModificacion === 'number' || fechaUltimaModificacion instanceof Date) {
            date = moment(fechaUltimaModificacion);
        } else if (typeof fechaUltimaModificacion === 'string' && fechaUltimaModificacion.length === 8) {
            date = moment(fechaUltimaModificacion, "YYYYMMDD");
        } else if (typeof fechaUltimaModificacion === 'string' && fechaUltimaModificacion.includes('/')) {
            date = moment(fechaUltimaModificacion, "DD/MM/YYYY");
        } else {
            date = moment(fechaUltimaModificacion);
        }
        return date.isValid() ? moment().startOf('day').diff(date.startOf('day'), 'days') : 0;
    } catch {
        return 0;
    }
};

const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function StockList() {
    const [quantityMaterials, setQuantityMaterials] = useState([])
    const [materials, setMaterials] = useState([])
    const [cuadrilla, setCuadrilla] = useState([])
    const [indexOpen, setIndexOpen] = useState([-1, -1])
    const [stockQuantity, setStockQuantity] = useState(0)
    const [openSnackbar, setOpenSnackbar] = useState({ open: false, message: '', type: 'success' })
    const [selectMaterials, setSelectMaterials] = useState(true)
    const [stockViewMode, setStockViewMode] = useState('stock')
    const [recalculatingPreStock, setRecalculatingPreStock] = useState(false)
    const [selectedMaterials, setSelectedMaterials] = useState([])
    const [selectedCuadrillas, setSelectedCuadrillas] = useState([])
    const [selectedEquiposCuadrillas, setSelectedEquiposCuadrillas] = useState([])
    const [hoveredCell, setHoveredCell] = useState({ row: -1, col: -1 })
    const [selectedCellDetails, setSelectedCellDetails] = useState(null)
    const [deleteDialog, setDeleteDialog] = useState({ open: false, cuadrillaId: null, serialId: null, serialTipo: null });
    const [initialLoad, setInitialLoad] = useState(true);

    const HIGHLIGHT_BG = '#e0e0e0'
    const HIGHLIGHT_BG_DARK = '#4a4a4a'
    const CROSSHAIR_BG = '#bdbdbd'

    const cuadrillaLabel = (c) => c?.usuario ? `${c.usuario} - ${c.numero}` : `${c?.numero ?? ''}`

    const getStockRecord = (materialId, cuadrillaId) => quantityMaterials.find(
        (c) => parseInt(c.idCuadrilla, 10) === cuadrillaId &&
            parseInt(c.idMaterial, 10) === materialId
    )

    const getStockValues = (materialId, cuadrillaId) => {
        const record = getStockRecord(materialId, cuadrillaId)
        const cantidad = record ? parseInt(record.cantidad, 10) || 0 : 0
        const preQuantity = record ? parseInt(record.preQuantity, 10) || 0 : 0
        return {
            cantidad,
            preQuantity,
            disponible: cantidad - preQuantity,
            exists: !!record,
        }
    }

    const selectedMaterialIds = new Set(selectedMaterials.map(m => m.id))
    const selectedCuadrillaIds = new Set(selectedCuadrillas.map(c => c.id))
    const selectedEquiposCuadrillaIds = new Set(selectedEquiposCuadrillas.map(c => c.id))

    const visibleMaterials = materials
        .map((m, originalIndex) => ({ ...m, originalIndex }))
        .filter(m => m?.visible && (selectedMaterialIds.size === 0 || selectedMaterialIds.has(m.id)))

    const visibleCuadrillas = cuadrilla
        .map((c, originalIndex) => ({ ...c, originalIndex }))
        .filter(c => selectedCuadrillaIds.size === 0 || selectedCuadrillaIds.has(c.id))

    const equiposCuadrillas = cuadrilla.filter(c =>
        c.id !== idCuadrillaNucleo &&
        (selectedEquiposCuadrillaIds.size === 0 || selectedEquiposCuadrillaIds.has(c.id))
    )

    const serialTotals = equiposCuadrillas.reduce((acc, c) => {
        (c?.seriales || []).forEach(s => {
            const tipo = (s?.tipo || '').trim().toLowerCase();
            if (tipo === 'ont') acc.ont += 1
            else if (tipo === 'flow') acc.flow += 1
            else if (tipo === 'ext') acc.ext += 1
        })
        return acc
    }, { ont: 0, flow: 0, ext: 0 })

    const fetchMaterialsCuadrilla = async () => {
        try {
            const materialsCollection = collection(firestore, 'materialsxcuadrilla');
            const querySnapshot = await getDocs(materialsCollection);

            // Transformar los documentos en un array de objetos
            const materialsCuad = querySnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id }));

            // Llamar a la función de cambio de datos
            setQuantityMaterials(materialsCuad);
        } catch (error) {
            console.error('Error al obtener los datos de materialsxcuadrilla:', error);
        }
    };

    useEffect(() => {
        // onValue(ref(database, '/materialsxcuadrilla/'), (snapshot) => {
        //     const materialsCuad = snapshot.val() ? Object.values(snapshot.val()) : [];
        //     onDataChange(materialsCuad)
        // }, {
        //     onlyOnce: true
        // });
        // onValue(ref(database, '/cuadrillas/'), (snapshot) => {
        //     setCuadrilla(Object.values(snapshot.val()))
        // }, {
        //     onlyOnce: true
        // });

        const loadAll = async () => {
            try {
                await Promise.all([
                    fetchMaterialsCuadrilla(),
                    getMaterialesOnce(setMaterials),
                    getCuadrillasOnce(setCuadrilla)
                ]);
            } catch (err) {
                console.error("Error loading stock data:", err);
            } finally {
                setInitialLoad(false);
            }
        };
        loadAll();
    }, []);

    const handleDeleteSerialClick = (cuadrillaId, serialId, serialTipo) => {
        setDeleteDialog({ open: true, cuadrillaId, serialId, serialTipo });
    };

    const handleDeleteSerialConfirm = async () => {
        const { cuadrillaId, serialId, serialTipo } = deleteDialog;
        try {
            const docRef = doc(firestore, "cuadrillas", String(cuadrillaId));
            const targetCuadrilla = cuadrilla.find(c => String(c.id) === String(cuadrillaId));
            if (!targetCuadrilla) return;

            const updatedSeriales = (targetCuadrilla.seriales || []).filter(
                s => !(String(s.id).trim() === String(serialId).trim() && (s?.tipo || '').trim().toLowerCase() === (serialTipo || '').trim().toLowerCase())
            );

            await updateDoc(docRef, { seriales: updatedSeriales });
            invalidateCuadrillasCache();

            setCuadrilla(prev => prev.map(c => 
                String(c.id) === String(cuadrillaId) 
                    ? { ...c, seriales: updatedSeriales }
                    : c
            ));

            setOpenSnackbar({ open: true, message: 'Serial eliminado correctamente', type: 'success' });
        } catch (error) {
            console.error('Error al eliminar el serial:', error);
            setOpenSnackbar({ open: true, message: 'Error al eliminar el serial', type: 'error' });
        } finally {
            setDeleteDialog({ open: false, cuadrillaId: null, serialId: null, serialTipo: null });
        }
    };


    const handleOpenModal = (materialId, cuadrillaId, cantidad) => {
        // Encontrar los índices correspondientes a los IDs para el modal
        const materialIndex = materials.findIndex(m => m.id === materialId);
        const cuadrillaIndex = cuadrilla.findIndex(c => c.id === cuadrillaId);
        const indexOpenModal = [materialIndex, cuadrillaIndex];
        setIndexOpen(indexOpenModal);
        setStockQuantity(cantidad);
    }

    const handleClose = () => {
        setIndexOpen([-1, -1]);
    }

    const onChangeValueCant = (e) => {
        const value = e.target.value;
        setStockQuantity(value);
    }

    const editStock = async (materialIndex, cuadrillaIndex, type) => {
        try {
            // Obtener los IDs reales a partir de los índices
            const materialId = materials[materialIndex]?.id;
            const cuadrillaId = cuadrilla[cuadrillaIndex]?.id;
            const cantidadNueva = parseInt(stockQuantity, 10);
            
            if (materialId === undefined || materialId === null || cuadrillaId === undefined || cuadrillaId === null) {
                console.error('Error: No se pudieron obtener los IDs reales', { materialIndex, cuadrillaIndex, materialId, cuadrillaId });
                return;
            }

            if (Number.isNaN(cantidadNueva)) {
                setOpenSnackbar({ open: true, message: 'Ingrese una cantidad válida', type: 'warning' });
                return;
            }

            // Filtrar cuadrilla usando los IDs reales
            const filterCuadrilla = quantityMaterials.filter(
                (c) => parseInt(c.idCuadrilla, 10) === cuadrillaId &&
                    parseInt(c.idMaterial, 10) === materialId
            );
            const stockActual = filterCuadrilla[0];
            const cantidadAnterior = stockActual ? parseInt(stockActual.cantidad, 10) || 0 : 0;
            const registroStock = {
                fecha: new Date().toISOString(),
                cantidad: cantidadNueva,
                cantidadAnterior,
                diferencia: cantidadNueva - cantidadAnterior,
                usuario: currentUser?.email || currentUser?.user || currentUser?.usuario || '-',
                tipo: type === 'edit' ? 'edicion' : 'creacion'
            };

            const docRef = doc(firestore, 'materialsxcuadrilla', `${cuadrillaId}c${materialId}`);

            if (type === 'edit') {
                // Editar documento existente
                await updateDoc(docRef, {
                    cantidad: cantidadNueva,
                    historialStock: arrayUnion(registroStock)
                });
                setIndexOpen([-1, -1]);
                setOpenSnackbar({ open: true, message: 'Cantidad editada correctamente', type: 'success' });
            } else {
                // Crear un nuevo documento
                const dataToStock = {
                    cantidad: cantidadNueva,
                    idCuadrilla: cuadrillaId,
                    idMaterial: materialId,
                    historialStock: [registroStock]
                };
                await setDoc(docRef, dataToStock);
                setIndexOpen([-1, -1]);
                setOpenSnackbar({ open: true, message: 'Cantidad guardada correctamente', type: 'success' });
            }
            
            // Recargar los datos después de la edición
            fetchMaterialsCuadrilla();
        } catch (e) {
            console.error('Error al editar o crear el stock:', e);
            setOpenSnackbar({ open: true, message: 'Error al actualizar el stock', type: 'error' });
        }
    };

    const handleCloseSanckbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setOpenSnackbar({ open: false, message: '', type: 'success' });
    };

    const handleRecalculatePreStock = async () => {
        setRecalculatingPreStock(true);
        try {
            await recalculatePreQuantityFromCuadrillas(cuadrilla);
            await fetchMaterialsCuadrilla();
            setOpenSnackbar({
                open: true,
                message: 'Pre-stock recalculado desde las OTs cargadas',
                type: 'success',
            });
        } catch (error) {
            console.error('Error al recalcular pre-stock:', error);
            setOpenSnackbar({
                open: true,
                message: 'Error al recalcular pre-stock',
                type: 'error',
            });
        } finally {
            setRecalculatingPreStock(false);
        }
    };

    const renderStockCellContent = (materialId, cuadrillaId, cantidad, preQuantity, disponible) => {
        if (stockViewMode === 'stock') {
            return cantidad;
        }

        return disponible;
    };

    const exportToExcel = () => {
        try {
            console.log('🔄 Iniciando exportación a Excel...');
            console.log('📊 Datos disponibles:', { 
                materials: materials.length, 
                cuadrillas: cuadrilla.length, 
                quantityMaterials: quantityMaterials.length 
            });

            // Filtrar cuadrillas excluyendo la cuadrilla con ID = 1
            const cuadrillasFiltered = cuadrilla.filter(c => c.id !== 1);
            console.log('🔍 Cuadrillas filtradas:', { 
                original: cuadrilla.length, 
                filtradas: cuadrillasFiltered.length, 
                excluidas: cuadrilla.length - cuadrillasFiltered.length 
            });
            
            // Crear estructura de la tabla
            const headers1 = ["Materiales", ...cuadrillasFiltered.map(c => c.usuario ? `${c.usuario} - ${c.numero}` : c.numero)];
            const rows1 = materials.map(material => {
                const row = [material.name];

                cuadrillasFiltered.forEach(cuadri => {
                    const { cantidad, preQuantity, disponible } = getStockValues(material.id, cuadri.id);
                    row.push(`${cantidad} | Pre: ${preQuantity} | Disp: ${disponible}`);
                });

                return row;
            });

            console.log('✅ Hoja 1 creada:', { headers: headers1.length, rows: rows1.length });

            // Crear el archivo XLSX
            const wsData = [headers1, ...rows1];
            const ws1 = utils.aoa_to_sheet(wsData);

            // **Segunda hoja: Seriales y Controles**
            const headers2 = ["Número", "ONT (Cantidad)", "FLOW (Cantidad)", "EXT (Cantidad)", "Android", "Android Reacondicionado"];
            const rows2 = []
            cuadrillasFiltered.forEach(cuadril => {
                const { numero, seriales = [], controles = [] } = cuadril;

                // Contar seriales en lugar de listarlos para evitar el límite de 32,767 caracteres
                const serialOntCount = seriales.filter(s => (s?.tipo || '').trim().toLowerCase() === "ont").length;
                const serialFlowCount = seriales.filter(s => (s?.tipo || '').trim().toLowerCase() === "flow").length;
                const serialExtCount = seriales.filter(s => (s?.tipo || '').trim().toLowerCase() === "ext").length;

                // Obtener controles
                const android = controles.find(c => c.name === "android")?.value || 0;
                const androidReac = controles.find(c => c.name === "android reacondicionado")?.value || 0;
                // const sagemcom = controles.find(c => c.name === "sagemcom")?.value || 0;
                // const sagemcomReac = controles.find(c => c.name === "sagemcom reacondicionado")?.value || 0;

                rows2.push([numero, serialOntCount, serialFlowCount, serialExtCount, android, androidReac]);
            });

            console.log('✅ Hoja 2 creada:', { headers: headers2.length, rows: rows2.length });

            const wsData2 = [headers2, ...rows2];
            const ws2 = utils.aoa_to_sheet(wsData2);

            // **Tercera hoja: Detalle de Seriales**
            const headers3 = ["Cuadrilla", "Tipo", "Serial ID", "Fecha Última Modificación"];
            const rows3 = [];
            
            cuadrillasFiltered.forEach(cuadril => {
                const { numero, seriales = [] } = cuadril;
                
                seriales.forEach(serial => {
                    rows3.push([
                        numero, 
                        serial.tipo?.toUpperCase() || "N/A", 
                        serial.id || "N/A",
                        serial.fechaUltimaModificacion || "No disponible"
                    ]);
                });
            });

            console.log('✅ Hoja 3 creada:', { headers: headers3.length, rows: rows3.length });

            const wsData3 = [headers3, ...rows3];
            const ws3 = utils.aoa_to_sheet(wsData3);


            // **Crear y descargar el archivo Excel**
            const wb = utils.book_new();
            utils.book_append_sheet(wb, ws1, "Stock materiales");
            utils.book_append_sheet(wb, ws2, "Resumen Seriales");
            utils.book_append_sheet(wb, ws3, "Detalle Seriales");

            console.log('✅ Workbook creado con 3 hojas');

            // Descargar el archivo usando el método correcto para navegadores
            console.log('🔄 Generando buffer del archivo...');
            const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
            console.log('✅ Buffer generado, tamaño:', excelBuffer.length);

            const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            console.log('✅ Blob creado, tamaño:', blob.size);

            const url = URL.createObjectURL(blob);
            console.log('✅ URL creada:', url);

            const link = document.createElement('a');
            link.href = url;
            link.download = 'stock_cba.xlsx';
            link.style.display = 'none'; // Asegurar que el link esté oculto
            
            document.body.appendChild(link);
            console.log('✅ Link agregado al DOM');
            
            link.click();
            console.log('✅ Click ejecutado');
            
            // Limpiar después de un pequeño delay
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
                console.log('✅ Limpieza completada');
            }, 100);

            console.log('🎉 Exportación completada exitosamente');
            
        } catch (error) {
            console.error('❌ Error en exportToExcel:', error);
            alert('Error al generar el archivo Excel: ' + error.message);
        }
    };


    return (
        <Container component="main" maxWidth="xl">
            <h4>Stock</h4>

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '84%', marginBottom: '20px' }}>
                        <Button variant="outlined" onClick={exportToExcel} endIcon={<FileDownloadIcon />}>
                    Descargar planilla
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'center', flexGrow: 1 }}>
                    <ButtonGroup variant="outlined" aria-label="outlined button group">
                        <Button
                            variant={selectMaterials ? 'contained' : 'outlined'}
                            onClick={() => setSelectMaterials(!selectMaterials)}
                        >
                            Materiales
                        </Button>
                        <Button
                            variant={!selectMaterials ? 'contained' : 'outlined'}
                            onClick={() => setSelectMaterials(!selectMaterials)}
                        >
                            Equipos
                        </Button>
                    </ButtonGroup>
                </Box>
            </Box>

            {selectMaterials && (
                <Box sx={{ display: 'flex', gap: 2, marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                    <ButtonGroup variant="outlined" size="small" aria-label="modo de vista stock">
                        <Button
                            variant={stockViewMode === 'stock' ? 'contained' : 'outlined'}
                            onClick={() => setStockViewMode('stock')}
                        >
                            Stock Real
                        </Button>
                        <Button
                            variant={stockViewMode === 'prestock' ? 'contained' : 'outlined'}
                            onClick={() => setStockViewMode('prestock')}
                        >
                            Disponible (Pre-stock)
                        </Button>
                    </ButtonGroup>
                   {/*  {currentUser?.rol === 'admin' && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={recalculatingPreStock}
                            onClick={handleRecalculatePreStock}
                        >
                            {recalculatingPreStock ? 'Recalculando...' : 'Recalcular pre-stock'}
                        </Button>
                    )} */}
                    <Autocomplete
                        multiple
                        size="small"
                        options={materials.filter(m => m?.visible)}
                        getOptionLabel={(m) => m?.name || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={selectedMaterials}
                        onChange={(_, newValue) => setSelectedMaterials(newValue)}
                        sx={{ minWidth: 320, flex: '1 1 320px' }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip size="small" label={option.name} {...getTagProps({ index })} key={option.id} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} placeholder={selectedMaterials.length === 0 ? 'Filtrar materiales...' : ''} />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={cuadrilla}
                        getOptionLabel={cuadrillaLabel}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={selectedCuadrillas}
                        onChange={(_, newValue) => setSelectedCuadrillas(newValue)}
                        sx={{ minWidth: 320, flex: '1 1 320px' }}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                                <Chip size="small" label={cuadrillaLabel(option)} {...getTagProps({ index })} key={option.id} />
                            ))
                        }
                        renderInput={(params) => (
                            <TextField {...params} placeholder={selectedCuadrillas.length === 0 ? 'Filtrar cuadrillas / usuarios...' : ''} />
                        )}
                    />
                    <Box sx={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>
                        {visibleMaterials.length} materiales · {visibleCuadrillas.length} cuadrillas
                    </Box>
                </Box>
            )}



            <div style={{ display: selectMaterials ? 'block' : 'none' }}>
                <TableContainer sx={{ maxHeight: 840 }} onMouseLeave={() => setHoveredCell({ row: -1, col: -1 })}>
                    <Table
                        stickyHeader
                        size="small"
                        sx={{
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            '& .MuiTableCell-root': {
                                border: '1px solid #d0d0d0',
                            },
                            '& .MuiTableCell-head': {
                                borderColor: '#000',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCell
                                    sx={{
                                        position: "sticky",
                                        background: "black",
                                        zIndex: 5,
                                        left: 0,
                                        top: 0,
                                        minWidth: 200,
                                        maxWidth: 240,
                                        padding: '8px 12px',
                                    }}
                                >
                                    Materiales
                                </StyledTableCell>
                                {visibleCuadrillas.map((cuadr, indexCuadrillas) => {
                                    const colHovered = hoveredCell.col === indexCuadrillas
                                    return (
                                        <StyledTableCell
                                            key={cuadr.id}
                                            sx={{
                                                minWidth: 130,
                                                maxWidth: 150,
                                                padding: '6px 8px',
                                                fontSize: 11,
                                                lineHeight: 1.2,
                                                fontWeight: 600,
                                                textAlign: 'center',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                verticalAlign: 'middle',
                                                background: colHovered ? HIGHLIGHT_BG_DARK : undefined,
                                            }}
                                        >
                                            {cuadr.usuario ? `${cuadr.usuario} - ${cuadr.numero}` : cuadr.numero}
                                        </StyledTableCell>
                                    )
                                })}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {initialLoad ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={visibleCuadrillas.length + 1} align="center" sx={{ py: 6 }}>
                                        <CircularProgress />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : visibleMaterials.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={visibleCuadrillas.length + 1} align="center" sx={{ py: 6 }}>
                                        <Typography variant="h6">No se encontraron resultados</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                visibleMaterials.map((value, indexMaterials) => {
                                const rowHovered = hoveredCell.row === indexMaterials
                                return (
                                    <StyledTableRow key={value.originalIndex}>
                                        <StyledTableCell
                                            sx={{
                                                position: "sticky",
                                                zIndex: 3,
                                                left: 0,
                                                background: rowHovered ? HIGHLIGHT_BG : "#efecec",
                                                minWidth: 200,
                                                maxWidth: 240,
                                                padding: '6px 10px',
                                                fontSize: 13,
                                                fontWeight: 500,
                                            }}
                                            onMouseEnter={() => setHoveredCell({ row: indexMaterials, col: -1 })}
                                        >
                                            {value?.name}
                                        </StyledTableCell>
                                        {visibleCuadrillas.map((cuadrillas, indexCuadrillas) => {
                                            const { cantidad, preQuantity, disponible, exists } = getStockValues(value.id, cuadrillas.id);
                                            const colHovered = hoveredCell.col === indexCuadrillas
                                            const isCross = rowHovered || colHovered
                                            const isExact = rowHovered && colHovered
                                            const dialogOpen = indexOpen[0] === value.originalIndex && indexOpen[1] === cuadrillas.originalIndex
                                            const hasDeficit = disponible < 0
                                            return (
                                                <StyledTableCell
                                                    key={indexCuadrillas}
                                                    onMouseEnter={() => setHoveredCell({ row: indexMaterials, col: indexCuadrillas })}
                                                    onClick={() => {
                                                        setSelectedCellDetails({
                                                            materialName: value?.name,
                                                            cuadrillaName: cuadrillaLabel(cuadrillas),
                                                            cantidad,
                                                            preQuantity,
                                                            disponible
                                                        });
                                                    }}
                                                    sx={{
                                                        padding: '4px 8px',
                                                        textAlign: 'center',
                                                        fontSize: 13,
                                                        background: isExact ? CROSSHAIR_BG : isCross ? HIGHLIGHT_BG : hasDeficit ? '#ffebee' : undefined,
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    {renderStockCellContent(value.id, cuadrillas.id, cantidad, preQuantity, disponible)}
                                                    {currentUser?.rol === 'admin' && stockViewMode === 'stock' &&
                                                        <IconButton
                                                            className="action__link"
                                                            size="small"
                                                            sx={{ padding: '2px', marginLeft: '4px' }}
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                handleOpenModal(value.id, cuadrillas.id, cantidad);
                                                            }}
                                                        >
                                                            <EditIcon sx={{ fontSize: 14 }} />
                                                        </IconButton>
                                                    }
                                                    <Dialog
                                                        open={dialogOpen}
                                                        aria-labelledby="form-dialog-title"
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        <DialogTitle
                                                            id="form-dialog-title"
                                                            onClose={handleClose}
                                                            sx={{ paddingBottom: 1 }}
                                                        >
                                                            Editar stock
                                                            <Box sx={{ fontSize: 13, color: '#666', fontWeight: 400, marginTop: '4px', lineHeight: 1.3 }}>
                                                                <strong>{value?.name}</strong>
                                                                <br />
                                                                {cuadrillaLabel(cuadrillas)}
                                                            </Box>
                                                        </DialogTitle>
                                                        <DialogContent>
                                                            <TextField
                                                                className="prod-input"
                                                                autoFocus
                                                                fullWidth
                                                                margin="dense"
                                                                name="cantidad"
                                                                label="Cantidad"
                                                                type="number"
                                                                value={stockQuantity}
                                                                onChange={onChangeValueCant}
                                                            />
                                                        </DialogContent>
                                                        <DialogActions>
                                                            <Button color="primary" onClick={handleClose}>
                                                                Cancelar
                                                            </Button>
                                                            <Button
                                                                color="primary"
                                                                disabled={stockQuantity === ''}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    editStock(value.originalIndex, cuadrillas.originalIndex, exists ? 'edit' : 'save');
                                                                }}
                                                            >
                                                                Aceptar
                                                            </Button>
                                                        </DialogActions>
                                                    </Dialog>
                                                </StyledTableCell>
                                            )
                                        })}
                                    </StyledTableRow>
                                )
                            }))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
            <div style={{ display: !selectMaterials ? 'block' : 'none' }}>
                    <Box sx={{ display: 'flex', gap: 2, marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={cuadrilla.filter(c => c.id !== idCuadrillaNucleo)}
                            getOptionLabel={cuadrillaLabel}
                            isOptionEqualToValue={(o, v) => o.id === v.id}
                            value={selectedEquiposCuadrillas}
                            onChange={(_, newValue) => setSelectedEquiposCuadrillas(newValue)}
                            sx={{ minWidth: 320, flex: '1 1 320px' }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <Chip size="small" label={cuadrillaLabel(option)} {...getTagProps({ index })} key={option.id} />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField {...params} placeholder={selectedEquiposCuadrillas.length === 0 ? 'Filtrar cuadrillas / usuarios...' : ''} />
                            )}
                        />
                        <Box sx={{
                            display: 'flex',
                            gap: 2,
                            alignItems: 'center',
                            padding: '8px 16px',
                            background: '#f5f5f5',
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                        }}>
                            <Box sx={{ fontSize: 12, color: '#666', fontWeight: 600 }}>Total general:</Box>
                            <Chip size="small" label={`ONT: ${serialTotals.ont}`} color="primary" variant="outlined" />
                            <Chip size="small" label={`FLOW: ${serialTotals.flow}`} color="secondary" variant="outlined" />
                            <Chip size="small" label={`EXT: ${serialTotals.ext}`} color="success" variant="outlined" />
                            <Chip size="small" label={`Σ: ${serialTotals.ont + serialTotals.flow + serialTotals.ext}`} />
                        </Box>
                    </Box>
                <TableContainer>
                    <Table
                        aria-label="simple table"
                        sx={{
                            borderCollapse: 'separate',
                            borderSpacing: 0,
                            '& .MuiTableCell-root': {
                                border: '1px solid #d0d0d0',
                            },
                            '& .MuiTableCell-head': {
                                borderColor: '#000',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <StyledTableCell sx={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}>Nombre</StyledTableCell>
                                <StyledTableCell align="center" sx={{ minWidth: '950px' }}>Seriales</StyledTableCell>
                                {/* <StyledTableCell>Controles</StyledTableCell> */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {initialLoad ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={2} align="center" sx={{ py: 6 }}>
                                        <CircularProgress />
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : equiposCuadrillas.length === 0 ? (
                                <StyledTableRow>
                                    <StyledTableCell colSpan={2} align="center" sx={{ py: 6 }}>
                                        <Typography variant="h6">No se encontraron resultados</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            ) : (
                                equiposCuadrillas.map((cuadrilla, index) => {
                                const serialFlowFilter = cuadrilla?.seriales?.filter(serial => (serial?.tipo || '').trim().toLowerCase() === 'flow')
                                const serialOntFilter = cuadrilla?.seriales?.filter(serial => (serial?.tipo || '').trim().toLowerCase() === 'ont')
                                const serialExtFilter = cuadrilla?.seriales?.filter(serial => (serial?.tipo || '').trim().toLowerCase() === 'ext')
                                return (
                                    <StyledTableRow
                                        key={index}
                                    >
                                        <StyledTableCell sx={{ width: '200px', minWidth: '200px', maxWidth: '200px', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                            {cuadrilla.usuario ? `${cuadrilla.usuario} - ${cuadrilla.numero}` : cuadrilla.numero}
                                        </StyledTableCell>
                                        <StyledTableCell>
                                            {cuadrilla?.seriales &&
                                                <div className="serial-container">
                                                    <div className="serial-type">
                                                        <span className="title-serial">ONT ({serialOntFilter?.length})</span>
                                                        <div>
                                                            {serialOntFilter?.map(serial => {
                                                                const dias = getSerialAge(serial.fechaUltimaModificacion);
                                                                const esAntiguo = dias > 30;
                                                                return (
                                                                <div key={serial.id} className="value-serial-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                                    {esAntiguo && (
                                                                        <Tooltip title={`Este serial tiene ${dias} días sin modificaciones`} arrow>
                                                                            <WarningAmberIcon fontSize="small" style={{ color: '#ed6c02', marginRight: '4px', flexShrink: 0 }} />
                                                                        </Tooltip>
                                                                    )}
                                                                    <div style={{ flexGrow: 1 }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                            <p className="value-serial" style={{ margin: 0, color: esAntiguo ? '#ed6c02' : undefined, fontWeight: esAntiguo ? 600 : undefined }}>
                                                                                {serial.id}
                                                                                {esAntiguo && <span style={{ fontSize: '0.75em', marginLeft: 6, fontStyle: 'italic' }}>({dias} días)</span>}
                                                                            </p>
                                                                            {String(cuadrilla.id) === '1' && (
                                                                                <span style={{
                                                                                    fontSize: '0.65rem',
                                                                                    fontWeight: 'bold',
                                                                                    marginLeft: '8px',
                                                                                    padding: '1px 6px',
                                                                                    borderRadius: '4px',
                                                                                    textTransform: 'uppercase',
                                                                                    backgroundColor: serial.tipoRemito === 'DEVOLUCION' ? '#fff3e0' : '#e8f5e9',
                                                                                    color: serial.tipoRemito === 'DEVOLUCION' ? '#e65100' : '#2e7d32',
                                                                                    border: `1px solid ${serial.tipoRemito === 'DEVOLUCION' ? '#ffcc80' : '#c8e6c9'}`
                                                                                }}>
                                                                                    {serial.tipoRemito === 'DEVOLUCION' ? 'Devolución' : 'Listo para usar'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {formatModificationDate(serial.fechaUltimaModificacion) && (
                                                                            <span className="modification-date" style={serialStyles.modificationDate}>
                                                                                {formatModificationDate(serial.fechaUltimaModificacion)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        color="error" 
                                                                        onClick={() => handleDeleteSerialClick(cuadrilla.id, serial.id, 'ont')}
                                                                        style={{ marginLeft: '8px', padding: '2px' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="serial-type">
                                                        <span className="title-serial">FLOW ({serialFlowFilter?.length})</span>
                                                        <div>
                                                            {serialFlowFilter?.map((serial, index) => {
                                                                const dias = getSerialAge(serial.fechaUltimaModificacion);
                                                                const esAntiguo = dias > 30;
                                                                return (
                                                                <div key={`${serial.id}-${index}`} className="value-serial-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                                    {esAntiguo && (
                                                                        <Tooltip title={`Este serial tiene ${dias} días sin modificaciones`} arrow>
                                                                            <WarningAmberIcon fontSize="small" style={{ color: '#ed6c02', marginRight: '4px', flexShrink: 0 }} />
                                                                        </Tooltip>
                                                                    )}
                                                                    <div style={{ flexGrow: 1 }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                            <p className="value-serial" style={{ margin: 0, color: esAntiguo ? '#ed6c02' : undefined, fontWeight: esAntiguo ? 600 : undefined }}>
                                                                                {serial.id}
                                                                                {esAntiguo && <span style={{ fontSize: '0.75em', marginLeft: 6, fontStyle: 'italic' }}>({dias} días)</span>}
                                                                            </p>
                                                                            {String(cuadrilla.id) === '1' && (
                                                                                <span style={{
                                                                                    fontSize: '0.65rem',
                                                                                    fontWeight: 'bold',
                                                                                    marginLeft: '8px',
                                                                                    padding: '1px 6px',
                                                                                    borderRadius: '4px',
                                                                                    textTransform: 'uppercase',
                                                                                    backgroundColor: serial.tipoRemito === 'DEVOLUCION' ? '#fff3e0' : '#e8f5e9',
                                                                                    color: serial.tipoRemito === 'DEVOLUCION' ? '#e65100' : '#2e7d32',
                                                                                    border: `1px solid ${serial.tipoRemito === 'DEVOLUCION' ? '#ffcc80' : '#c8e6c9'}`
                                                                                }}>
                                                                                    {serial.tipoRemito === 'DEVOLUCION' ? 'Devolución' : 'Listo para usar'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {formatModificationDate(serial.fechaUltimaModificacion) && (
                                                                            <span className="modification-date" style={serialStyles.modificationDate}>
                                                                                {formatModificationDate(serial.fechaUltimaModificacion)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        color="error" 
                                                                        onClick={() => handleDeleteSerialClick(cuadrilla.id, serial.id, 'flow')}
                                                                        style={{ marginLeft: '8px', padding: '2px' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                    <div className="serial-type">
                                                        <span className="title-serial">EXT ({serialExtFilter?.length})</span>
                                                        <div>
                                                            {serialExtFilter?.map(serial => {
                                                                const dias = getSerialAge(serial.fechaUltimaModificacion);
                                                                const esAntiguo = dias > 30;
                                                                return (
                                                                <div key={serial.id} className="value-serial-container" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%', padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                                    {esAntiguo && (
                                                                        <Tooltip title={`Este serial tiene ${dias} días sin modificaciones`} arrow>
                                                                            <WarningAmberIcon fontSize="small" style={{ color: '#ed6c02', marginRight: '4px', flexShrink: 0 }} />
                                                                        </Tooltip>
                                                                    )}
                                                                    <div style={{ flexGrow: 1 }}>
                                                                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                                                            <p className="value-serial" style={{ margin: 0, color: esAntiguo ? '#ed6c02' : undefined, fontWeight: esAntiguo ? 600 : undefined }}>
                                                                                {serial.id}
                                                                                {esAntiguo && <span style={{ fontSize: '0.75em', marginLeft: 6, fontStyle: 'italic' }}>({dias} días)</span>}
                                                                            </p>
                                                                            {String(cuadrilla.id) === '1' && (
                                                                                <span style={{
                                                                                    fontSize: '0.65rem',
                                                                                    fontWeight: 'bold',
                                                                                    marginLeft: '8px',
                                                                                    padding: '1px 6px',
                                                                                    borderRadius: '4px',
                                                                                    textTransform: 'uppercase',
                                                                                    backgroundColor: serial.tipoRemito === 'DEVOLUCION' ? '#fff3e0' : '#e8f5e9',
                                                                                    color: serial.tipoRemito === 'DEVOLUCION' ? '#e65100' : '#2e7d32',
                                                                                    border: `1px solid ${serial.tipoRemito === 'DEVOLUCION' ? '#ffcc80' : '#c8e6c9'}`
                                                                                }}>
                                                                                    {serial.tipoRemito === 'DEVOLUCION' ? 'Devolución' : 'Listo para usar'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        {formatModificationDate(serial.fechaUltimaModificacion) && (
                                                                            <span className="modification-date" style={serialStyles.modificationDate}>
                                                                                {formatModificationDate(serial.fechaUltimaModificacion)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <IconButton 
                                                                        size="small" 
                                                                        color="error" 
                                                                        onClick={() => handleDeleteSerialClick(cuadrilla.id, serial.id, 'ext')}
                                                                        style={{ marginLeft: '8px', padding: '2px' }}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                </div>
                                            }
                                        </StyledTableCell>
                                        {/* <StyledTableCell>{cuadrilla?.controles?.map(control => (
                                            <div>
                                                <span>{control.name?.toUpperCase()} : </span>
                                                <span> {control.value}</span>
                                            </div>
                                        ))
                                        }
                                        </StyledTableCell> */}
                                    </StyledTableRow>
                                )
                            }))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>

            {/* Dialog de Detalles de Stock */}
            <Dialog
                open={!!selectedCellDetails}
                onClose={() => setSelectedCellDetails(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Detalle de Stock
                    <Box sx={{ fontSize: 13, color: '#666', fontWeight: 400, marginTop: '4px', lineHeight: 1.3 }}>
                        <strong>{selectedCellDetails?.materialName}</strong>
                        <br />
                        {selectedCellDetails?.cuadrillaName}
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Stock Real:</Typography>
                            <Typography variant="body1" fontWeight={600}>{selectedCellDetails?.cantidad}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', pb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Pre-stock (Comprometido):</Typography>
                            <Typography variant="body1" fontWeight={600} color="warning.main">{selectedCellDetails?.preQuantity}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                            <Typography variant="body2" color="text.secondary">Stock Disponible:</Typography>
                            <Typography 
                                variant="body1" 
                                fontWeight={700} 
                                color={selectedCellDetails?.disponible < 0 ? 'error.main' : 'success.main'}
                            >
                                {selectedCellDetails?.disponible}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedCellDetails(null)} variant="contained" color="primary">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog de confirmación de eliminación de serial */}
            <Dialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, cuadrillaId: null, serialId: null, serialTipo: null })}
            >
                <DialogTitle>Confirmar eliminación</DialogTitle>
                <DialogContent>
                    <Typography>
                        ¿Estás seguro de que deseas eliminar el serial <strong>{deleteDialog.serialId}</strong> ({deleteDialog.serialTipo?.toUpperCase()}) de esta cuadrilla?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog({ open: false, cuadrillaId: null, serialId: null, serialTipo: null })} color="inherit">
                        Cancelar
                    </Button>
                    <Button onClick={handleDeleteSerialConfirm} color="error" variant="contained">
                        Eliminar
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={openSnackbar.open} autoHideDuration={2000} onClose={handleCloseSanckbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSanckbar} severity={openSnackbar.type} sx={{ width: '100%' }}>
                    {openSnackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default StockList;
